import React, { useContext, useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton, Link } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  IDocumentMediaPreviewData,
  MESSAGE_CONTENT_TYPE,
} from "@home-health-notify/domain";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { ActionIcons } from "../mediaViewer/MediaViewer.style";
import { getBlobUrl, printDocument, printImage } from "./helper";
import { ApiErrorHandler, OrganizationContext } from "hhn-commons";
import { ErrorMessage } from "../../utils/ErrorMessage";
import { useSnackbar } from "notistack";

type MediaPreviewActionsType = {
  activeMedia: IDocumentMediaPreviewData;
  onDialogClose: () => void;
  mediaUrl: string;
  docUrl: string;
};
const MediaPreviewActions = ({
  activeMedia,
  onDialogClose,
  mediaUrl,
  docUrl,
}: MediaPreviewActionsType) => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedOrgUser } = useContext(OrganizationContext);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadMediaUrl, setDownloadMediaUrl] = useState<string>("");

  const downloadRef = useRef(null);
  const messageContentType = activeMedia && activeMedia.mediaType;
  const isDocument = messageContentType === MESSAGE_CONTENT_TYPE.DOCUMENT;
  const downloadMedia = async () => {
    try {
      setIsDownloading(true);
      if (!selectedOrgUser) {
        throw new Error("No organization user selected");
      }
      const blobUrl = await getBlobUrl(activeMedia, selectedOrgUser);
      setDownloadMediaUrl(blobUrl);
      setIsDownloading(false);
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar, ErrorMessage.DOWNLOAD_FAILED);
      setIsDownloading(false);
    }
  };

  const handlePrint = () => {
    try {
      if (isDocument) {
        if (docUrl) {
          printDocument(docUrl);
        } else {
          throw new Error("No document URL provided");
        }
      } else {
        if (mediaUrl) {
          printImage(mediaUrl);
        } else {
          throw new Error("No media URL provided");
        }
      }
    } catch (e) {
      ApiErrorHandler(
        e,
        enqueueSnackbar,
        isDocument
          ? ErrorMessage.DOCUMENT_NOT_FOUND
          : ErrorMessage.IMAGE_NOT_FOUND,
      );
    }
  };

  useEffect(() => {
    if (downloadMediaUrl && downloadRef.current) {
      (downloadRef.current as HTMLAnchorElement).click();
      setDownloadMediaUrl("");
    }
  }, [downloadMediaUrl]);

  return (
    <ActionIcons>
      {downloadMediaUrl && (
        <Link
          href={downloadMediaUrl}
          ref={downloadRef}
          target="_blank"
          download=""
        />
      )}
      {!isDocument && (
        <>
          <IconButton
            edge="start"
            color="inherit"
            onClick={downloadMedia}
            aria-label="download"
            disabled={isDownloading}
          >
            {isDownloading ? (
              <CircularProgress size="1.5rem" color="inherit" />
            ) : (
              <DownloadIcon data-testid="download-button" />
            )}
          </IconButton>
          <IconButton
            data-testid="print-button"
            edge="start"
            color="inherit"
            onClick={handlePrint}
            aria-label="print"
            disabled={messageContentType !== MESSAGE_CONTENT_TYPE.IMAGE}
          >
            <PrintIcon />
          </IconButton>
        </>
      )}
      <IconButton
        edge="start"
        color="inherit"
        onClick={onDialogClose}
        data-testid="close-dialog"
        aria-label="close"
        id="document-media-viewer-close-button"
      >
        <CloseIcon id="document-media-viewer-close-icon" />
        {/* Don't remove above id. This is used in UseOutsideClick.ts file*/}
      </IconButton>
    </ActionIcons>
  );
};

export default MediaPreviewActions;
